import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiX, HiCalculator } from 'react-icons/hi';
import { trackPlanetGuideResult } from '../utils/analytics';
import useScrollLock from '../utils/useScrollLock';
import '../styles/WearGuide.css';

/* Numerology's Mulank: reduce the DAY of birth to a single digit. The
   month and year play no part. */
const birthNumber = (isoDate) => {
  if (!isoDate) return null;

  const day = parseInt(String(isoDate).split('-')[2], 10);
  if (!Number.isFinite(day)) return null;

  let sum = day;
  while (sum > 9) {
    sum = String(sum)
      .split('')
      .reduce((total, digit) => total + Number(digit), 0);
  }

  return sum;
};

/* The dates that suit a birth number are simply the days of the month
   that reduce to it — 2 gives the 2nd, 11th, 20th and 29th. Worked out
   from the same rule as the number itself rather than kept as a typed
   list, so the two can't drift apart. */
const datesFor = (number) => {
  const days = [];
  for (let day = 1; day <= 31; day += 1) {
    if (birthNumber(`2000-01-${String(day).padStart(2, '0')}`) === number) {
      days.push(day);
    }
  }
  return days;
};

const PLANETS = {
  1: { name: 'Sun', emoji: '☀️' },
  2: { name: 'Moon', emoji: '🌙' },
  3: { name: 'Jupiter', emoji: '🟡' },
  4: { name: 'Rahu', emoji: '🟠' },
  5: { name: 'Mercury', emoji: '💚' },
  6: { name: 'Venus', emoji: '💗' },
  7: { name: 'Ketu', emoji: '🔵' },
  8: { name: 'Saturn', emoji: '🟣' },
  9: { name: 'Mars', emoji: '🔴' },
};

/**
 * Everything a customer needs after the parcel arrives: cleansing,
 * intention, the date to start wearing it, which wrist, and when.
 *
 * The birth-number calculator that used to be its own "find my planet"
 * modal now lives inside step 3, where it is actually useful — the
 * number it returns is what decides the wearing date, and the ruling
 * planet falls out of the same figure.
 */
export default function WearGuide({ isOpen, onClose }) {
  const [birthDate, setBirthDate] = useState('');
  const [result, setResult] = useState(null); // { number, dates, planet }

  useScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const calculate = () => {
    const number = birthNumber(birthDate);
    if (!number) return;

    setResult({ number, dates: datesFor(number), planet: PLANETS[number] });

    // Which planets people are told they belong to — the closest thing
    // the site has to a demand signal for stock planning.
    trackPlanetGuideResult(PLANETS[number]?.name, number);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="wg-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="wg-sheet"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="How to wear your gemstone bracelet"
        >
          <header className="wg-head">
            <div>
              <span className="wg-eyebrow">Your Guide</span>
              <h2>How to Wear Your Gemstone Bracelet</h2>
            </div>
            <button className="wg-close" onClick={onClose} aria-label="Close">
              <HiX />
            </button>
          </header>

          <div className="wg-body">
            {/* ---------------------- 1 ---------------------- */}
            <section className="wg-step">
              <span className="wg-num">1</span>
              <div className="wg-step-body">
                <h3>Cleanse Your Bracelet</h3>
                <p>
                  After receiving your bracelet, gently wash it with holy water
                  before wearing it for the first time. Pat it dry with a soft,
                  clean cloth.
                </p>
              </div>
            </section>

            {/* ---------------------- 2 ---------------------- */}
            <section className="wg-step">
              <span className="wg-num">2</span>
              <div className="wg-step-body">
                <h3>Set Your Intention</h3>
                <p>
                  Before wearing your bracelet, hold it gently in your hands for
                  a moment and set a positive intention. Think about what you
                  would like to focus on, such as peace, confidence, abundance,
                  protection, emotional balance, or any personal wish that
                  inspired you to wear the bracelet.
                </p>
              </div>
            </section>

            {/* ---------------------- 3 ---------------------- */}
            <section className="wg-step">
              <span className="wg-num">3</span>
              <div className="wg-step-body">
                <h3>Choose Your Wearing Date</h3>
                <p>
                  You can begin wearing your bracelet on a date that matches
                  your Birth Number (Mulank). To calculate your Birth Number,
                  add the digits of your birth date until you get a single
                  number.
                </p>

                {/* The old "find my planet" modal, folded in where the
                    number it works out actually gets used. */}
                <div className="wg-calc">
                  <h4>
                    <HiCalculator /> Work it out for me
                  </h4>
                  <p className="wg-calc-note">
                    Enter your date of birth — we'll find your Birth Number,
                    the dates that suit you, and your ruling planet.
                  </p>

                  <div className="wg-calc-row">
                    <input
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      aria-label="Date of birth"
                    />
                    <button onClick={calculate} disabled={!birthDate}>
                      Calculate
                    </button>
                  </div>

                  {result && (
                    <motion.div
                      className="wg-result"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="wg-result-head">
                        <span className="wg-result-num">{result.number}</span>
                        <div>
                          <strong>Your Birth Number is {result.number}</strong>
                          <span>
                            {result.planet.emoji} Ruled by {result.planet.name}
                          </span>
                        </div>
                      </div>

                      <p className="wg-result-dates">
                        Begin wearing it on the{' '}
                        <strong>{result.dates.join(', ')}</strong> of any month.
                      </p>
                    </motion.div>
                  )}
                </div>

              </div>
            </section>

            {/* ---------------------- 4 ---------------------- */}
            <section className="wg-step">
              <span className="wg-num">4</span>
              <div className="wg-step-body">
                <h3>Choose the Correct Wrist</h3>
                <p>
                  Wear your gemstone bracelet on the wrist recommended by your
                  Astrologer. If you have not received any specific guidance,
                  you can wear it on your non-dominant hand.
                </p>
              </div>
            </section>

            {/* ---------------------- 5 ---------------------- */}
            <section className="wg-step">
              <span className="wg-num">5</span>
              <div className="wg-step-body">
                <h3>Best Time to Wear Your Bracelet</h3>
                <p>
                  Wear your bracelet in the morning after bathing, preferably
                  between 4:00 a.m. and 8:00 a.m. Choose a quiet moment when
                  you feel relaxed and focused. You may also wear it during
                  your recommended prayer or meditation time.
                </p>
              </div>
            </section>

            <p className="wg-tip">
              <strong>Tip:</strong> If your Astrologer has provided specific
              instructions about cleansing, the wearing date, time, or wrist,
              always follow their guidance.
            </p>
          </div>

          <footer className="wg-foot">
            <button onClick={onClose}>Got it</button>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
